import { RequestQueryParamsModel } from "@/models/interface/http.interface";
import { InvoiceRecurringReportResponseDto } from "@/models/interface/invoice-recurring-report/InvoiceRecurringReportResponseDto.interface";
import { invoiceServices } from "@/services/invoice.service";

import { SearchBuilder } from "@/builder";
import { EnumBooleanInString } from "@/models/enums/global.enum";
import store from "@/store";
import { LabelInValue } from "@/types";
import { Moment } from "moment";
import useDate from "./useDate";

export type FilterField = Partial<{
  branch: LabelInValue | null;
  customer: LabelInValue | null;
  invoiceArNo: string | null;
  unitCode: string | null;
  icNo: LabelInValue | null;
  icStatus: string | null;
  billingDate: Array<Moment> | null;
  isBackup: EnumBooleanInString | undefined;
  invoiceAr: string | null;
}>;

const useInvoiceRecurringReport = () => {
  const findAll = (
    params?: RequestQueryParamsModel
  ): Promise<InvoiceRecurringReportResponseDto[]> => {
    return invoiceServices.getInvoiceRecurringReport({
      ...params,
      sorts: "customerName:asc, billingDate:asc",
    });
  };

  const download = (params?: RequestQueryParamsModel) => {
    return invoiceServices.downloadReportInvoiceRecurringReport({
      ...params,
      sorts: "customerName:asc, billingDate:asc",
    });
  };

  const filterBy = (field: FilterField): string => {
    const { toStartDay, toEndDay } = useDate();
    const builder = new SearchBuilder();
    const query: Array<string> = [];

    if (field.branch) {
      query.push(builder.push(["branchId", field.branch.key]).build());
    }

    if (field.customer) {
      query.push(builder.push(["customerId", field.customer.key]).build());
    }

    if (field.invoiceArNo) {
      query.push(
        builder
          .push(["invoiceArNo", field.invoiceArNo], { like: "both" })
          .build()
      );
    }

    if (field.unitCode) {
      query.push(
        builder.push(["unitCode", field.unitCode], { like: "both" }).build()
      );
    }

    if (field.icNo) {
      query.push(builder.push(["icId", field.icNo.key]).build());
    }

    if (field.icStatus) {
      query.push(builder.push(["icStatus", field.icStatus]).build());
    }

    if (field.billingDate && field.billingDate.length > 0) {
      const [start, end] = field.billingDate;
      query.push(
        builder
          .push(["billingStart", toStartDay(start).format()], {
            het: true,
          })
          .and()
          .push(["billingEnd", toEndDay(end).format()], {
            let: true,
          })
          .build()
      );
    }

    if (field.isBackup) {
      query.push(builder.push(["isBackup", field.isBackup]).build());
    }

    return query.join(builder.AND);
  };

  const buildReportHeader = (form: FilterField): string => {
    const backupUnitValue = () => {
      if (!form.isBackup) return "ALL";
      if (form.isBackup === "true") return "YES";

      return "NO";
    };
    const { toDefaultFormat } = useDate();
    const company: string =
      store.state["authStore"].authData.companyName || "ALL";
    const branch: string = form.branch?.label || "ALL";
    const customer: string = form.customer?.label || "ALL";
    const invoiceArNo: string = form.invoiceArNo || "ALL";
    const invoiceAr: string = form.invoiceAr || "ALL";
    const unitCode: string = form.unitCode || "ALL";
    const icNo: string = form.icNo?.label || "ALL";
    const icStatus: string = form.icStatus || "ALL";
    const byBackupUnit: string = backupUnitValue();
    let dateFrom = "ALL";
    let dateTo = "ALL";

    if (form.billingDate && form.billingDate.length === 2) {
      const [start, end] = form.billingDate;
      dateFrom = toDefaultFormat(start);
      dateTo = toDefaultFormat(end);
    }

    const header: Array<string> = [
      company,
      branch,
      customer,
      dateFrom,
      dateTo,
      unitCode,
      icNo,
      icStatus,
      invoiceArNo,
      invoiceAr,
      byBackupUnit,
    ];

    return header.join(",");
  };

  return { findAll, buildReportHeader, download, filterBy };
};

export default useInvoiceRecurringReport;
